import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, TrendingUp, Activity, AlertCircle, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import usePullToRefresh from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_outstanding: 0,
    total_repaid: 0,
    active_advances: 0,
    due_this_month: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ short_term: [], long_term: [] });
  const [searching, setSearching] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletePin, setDeletePin] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    toast.info('Refreshing...');
    await fetchStats();
    toast.success('Data refreshed!');
  }, [fetchStats]);

  const { pullDistance, isRefreshing } = usePullToRefresh(handleRefresh);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults({ short_term: [], long_term: [] });
      setSearchPerformed(false);
      return;
    }

    setSearching(true);
    setSearchPerformed(true);
    try {
      const response = await axios.get(`${API}/search?employee=${searchQuery}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Failed to search');
    } finally {
      setSearching(false);
    }
  };

  const openDeleteDialog = (item, type) => {
    setItemToDelete({ ...item, type });
    setDeletePin('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deletePin !== '1997') {
      toast.error('Invalid PIN');
      return;
    }
    
    try {
      const endpoint = itemToDelete.type === 'short' 
        ? `${API}/short-term-advances/${itemToDelete.id}`
        : `${API}/long-term-advances/${itemToDelete.id}`;
      
      await axios.delete(endpoint, {
        data: { pin: deletePin }
      });
      
      toast.success('Record deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDeletePin('');
      handleSearch();
      fetchStats();
    } catch (error) {
      console.error('Error deleting:', error);
      if (error.response?.status === 403) {
        toast.error('Invalid PIN');
      } else {
        toast.error('Failed to delete record');
      }
    }
  };

  const metrics = [
    {
      title: 'Total Outstanding',
      value: `₹${stats.total_outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: 'text-orange-600',
      bg: 'bg-gradient-to-br from-orange-100 to-orange-50',
      border: 'border-orange-200',
      shadow: 'shadow-orange-100',
      testId: 'metric-total-outstanding'
    },
    {
      title: 'Total Repaid',
      value: `₹${stats.total_repaid.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: TrendingUp,
      color: 'text-emerald-600',
      bg: 'bg-gradient-to-br from-emerald-100 to-emerald-50',
      border: 'border-emerald-200',
      shadow: 'shadow-emerald-100',
      testId: 'metric-total-repaid'
    },
    {
      title: 'Active Advances',
      value: stats.active_advances,
      icon: Activity,
      color: 'text-blue-600',
      bg: 'bg-gradient-to-br from-blue-100 to-blue-50',
      border: 'border-blue-200',
      shadow: 'shadow-blue-100',
      testId: 'metric-active-advances'
    },
    {
      title: 'Due This Month',
      value: stats.due_this_month,
      icon: AlertCircle,
      color: 'text-rose-600',
      bg: 'bg-gradient-to-br from-rose-100 to-rose-50',
      border: 'border-rose-200',
      shadow: 'shadow-rose-100',
      testId: 'metric-due-this-month'
    }
  ];

  const hasSearchResults = searchResults.short_term.length > 0 || searchResults.long_term.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="dashboard-loading">
        <div className="text-blue-700">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-in" data-testid="dashboard">
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="mb-8">
        <h1 className="text-4xl font-extrabold tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">Dashboard</h1>
        <p className="text-base text-blue-700/80">Overview of salary advances and repayments</p>
      </div>

      <div className="mb-8 glass-card rounded-2xl p-8 shadow-xl">
        <h2 className="text-2xl font-semibold tracking-tight text-blue-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/short-term"
            data-testid="quick-action-short-term"
            className="block p-5 border-2 border-blue-200 rounded-xl hover:bg-blue-50 hover:border-blue-400 hover:shadow-lg transition-all hover:-translate-y-1"
          >
            <h3 className="font-semibold text-blue-900 mb-1">New Short-term Advance</h3>
            <p className="text-sm text-blue-600">Record a new salary advance</p>
          </a>
          <a
            href="/long-term"
            data-testid="quick-action-long-term"
            className="block p-5 border-2 border-purple-200 rounded-xl hover:bg-purple-50 hover:border-purple-400 hover:shadow-lg transition-all hover:-translate-y-1"
          >
            <h3 className="font-semibold text-purple-900 mb-1">New Long-term Advance</h3>
            <p className="text-sm text-purple-600">Setup monthly installments</p>
          </a>
          <div className="p-5 border-2 border-cyan-200 rounded-xl bg-cyan-50/50">
            <h3 className="font-semibold text-cyan-900 mb-3">Search & Delete Employee Records</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-cyan-500" size={18} />
              <Input
                data-testid="dashboard-search"
                placeholder="Enter employee name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 border-cyan-300 focus:border-cyan-500"
              />
            </div>
            <Button 
              onClick={handleSearch}
              className="w-full mt-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg text-sm"
              disabled={searching}
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </div>
      </div>

      {hasSearchResults && (
        <div className="mb-8 glass-card rounded-2xl p-8 shadow-xl">
          <h2 className="text-2xl font-semibold tracking-tight text-blue-900 mb-4">Search Results for "{searchQuery}"</h2>
          
          {searchResults.short_term.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">Short-term Advances</h3>
              <div className="space-y-2">
                {searchResults.short_term.map((advance) => (
                  <div key={advance.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200 hover:shadow-md transition-all">
                    <div className="flex-1">
                      <p className="font-semibold text-blue-900">{advance.employee_name}</p>
                      <p className="text-sm text-blue-600">Amount: ₹{advance.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })} | Date: {advance.advance_date}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        advance.status === 'pending' 
                          ? 'bg-amber-100 text-amber-700 border border-amber-300'
                          : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                      }`}>
                        {advance.status === 'pending' ? 'Pending' : 'Repaid'}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => openDeleteDialog(advance, 'short')}
                      className="ml-4"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {searchResults.long_term.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-3">Long-term Advances</h3>
              <div className="space-y-2">
                {searchResults.long_term.map((advance) => {
                  const paidCount = advance.installments?.filter(i => i.paid).length || 0;
                  const totalCount = advance.installments?.length || 0;
                  return (
                    <div key={advance.id} className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200 hover:shadow-md transition-all">
                      <div className="flex-1">
                        <p className="font-semibold text-purple-900">{advance.employee_name}</p>
                        {advance.description && (
                          <p className="text-sm text-purple-600 italic">"{advance.description}"</p>
                        )}
                        <p className="text-sm text-purple-600">
                          Total: ₹{advance.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })} | 
                          Progress: {paidCount}/{totalCount} installments
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => openDeleteDialog(advance, 'long')}
                        className="ml-4"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {searchPerformed && !hasSearchResults && !searching && (
        <div className="mb-8 glass-card rounded-2xl p-8 shadow-xl text-center text-blue-600">
          No results found for "{searchQuery}"
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div
              key={metric.title}
              data-testid={metric.testId}
              className={`glass-card rounded-2xl ${metric.border} p-6 hover:shadow-2xl hover:-translate-y-1 transition-all ${metric.shadow} group relative overflow-hidden`}
            >
              <div className={`absolute inset-0 ${metric.bg} opacity-50`}></div>
              <div className="relative flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-700/70 mb-2">{metric.title}</p>
                  <p className={`text-3xl font-bold ${metric.color} tabular-nums`}>{metric.value}</p>
                </div>
                <div className={`${metric.bg} ${metric.color} p-3 rounded-xl shadow-lg`}>
                  <Icon size={24} />
                </div>
              </div>
            </div>
          );
        })}      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="glass-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-blue-900">Delete {itemToDelete?.type === 'short' ? 'Short-term' : 'Long-term'} Advance</AlertDialogTitle>
            <AlertDialogDescription className="text-blue-700">
              This action cannot be undone. Please enter PIN to confirm deletion of <strong>{itemToDelete?.employee_name}</strong>'s record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Label htmlFor="delete_pin" className="text-blue-900">Enter PIN</Label>
            <Input
              id="delete_pin"
              type="password"
              placeholder="Enter PIN"
              value={deletePin}
              onChange={(e) => setDeletePin(e.target.value)}
              data-testid="delete-pin-input"
              className="border-blue-200 focus:border-blue-500"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletePin('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete"
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;